import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { fetchClientPdfPreview } from "@/api/clientPdf";
import { ClientPdfPanel } from "@/components/projects/diagnostic/ClientPdfPanel";
import { useSidebar } from "@/components/ui/sidebar";
import {
  CLIENT_PDF_SECTIONS,
  type ClientPdfSection,
} from "@/components/projects/diagnostic/clientPdfSections";

/**
 * A tela "PDF para o cliente" (#1772, EF-DIAG-005 RF-01), no endereço
 * `/projects/:projectId/diagnostic/pdf`, como no HTML do modelo: o painel de
 * montagem à esquerda e a prévia ocupando o resto. O caminho no topo
 * ("Projetos / <projeto> / Diagnóstico / PDF para o cliente") vem da barra do
 * myGROW (`useShellBreadcrumbs`).
 *
 * Só chega aqui do comercial para cima: a rota está no catálogo com esse mínimo
 * (`project-nav.ts`), como a rota do PDF no servidor.
 */
export default function ClientPdfPage() {
  const { t } = useTranslation();
  useCollapsedMenu();
  const { projectId = "" } = useParams<{ projectId: string }>();
  const [selected, setSelected] = useState<Set<ClientPdfSection>>(
    () => new Set(CLIENT_PDF_SECTIONS.map((section) => section.key)),
  );
  // Na ordem das páginas, e não na ordem dos cliques.
  const sections = useMemo(
    () => CLIENT_PDF_SECTIONS.map((section) => section.key).filter((key) => selected.has(key)),
    [selected],
  );
  const preview = useClientPdfPreview(projectId, sections);
  // Sem `preview`: este é o arquivo de verdade, e a rota registra quem gerou.
  const downloadHref =
    sections.length > 0
      ? `/api/projects/${projectId}/diagnostic/client-pdf?${sections
          .map((section) => `sections=${section}`)
          .join("&")}`
      : null;

  const toggle = (section: ClientPdfSection, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(section);
      else next.delete(section);
      return next;
    });

  return (
    // O `-m-6` desfaz o respiro do `<main>`: a tela vai de borda a borda, como no
    // modelo, e o painel e a prévia rolam cada um por conta própria.
    <div className="-m-6 flex h-[calc(100%+3rem)] min-h-0">
      <ClientPdfPanel
        selected={selected}
        onToggle={toggle}
        pages={preview.status === "ready" && preview.shown ? preview.shown.pages : null}
        downloadHref={downloadHref}
        backHref={`/projects/${projectId}/diagnostic`}
      />
      <section
        data-testid="client-pdf-preview"
        className="relative flex flex-1 items-center justify-center bg-[#7f8a99]"
      >
        {preview.shown && preview.status !== "empty" ? (
          <>
            {/* A barra do visualizador sai: as páginas aparecem uma embaixo da outra. */}
            <iframe
              title={t("Preview of the client PDF")}
              src={`${preview.shown.url}#toolbar=0&navpanes=0`}
              className="h-full w-full border-0"
            />
            {preview.status !== "ready" && (
              <p className="absolute right-4 top-4 rounded-md bg-white/95 px-3 py-1.5 text-xs text-[#08254c] shadow">
                {preview.status === "error"
                  ? t("Could not build the preview. Try again in a moment.")
                  : t("Updating the preview…")}
              </p>
            )}
          </>
        ) : (
          <p className="rounded-md bg-white/90 px-4 py-3 text-sm text-[#08254c]">
            {preview.status === "empty"
              ? t("No section selected: the PDF would only have the cover.")
              : preview.status === "error"
                ? t("Could not build the preview. Try again in a moment.")
                : t("Building the preview…")}
          </p>
        )}
      </section>
    </div>
  );
}

type Shown = { url: string; pages: number };

type Preview = {
  /** O PDF na tela: continua o anterior enquanto o novo é montado. */
  shown: Shown | null;
  status: "loading" | "ready" | "error" | "empty";
};

/** Quanto esperar depois do último clique antes de pedir a prévia de novo. */
const PREVIEW_DELAY_MS = 500;

/**
 * A prévia é o PDF da própria rota (a issue pede "sem um segundo layout no
 * front"), pedido de novo a cada combinação de seções nova.
 *
 * Para não incomodar quem marca e desmarca (pedido do Maycon, 29/10/2026): o PDF
 * anterior fica na tela enquanto o novo é montado, e cada combinação já vista fica
 * guardada, então voltar a ela é instantâneo. Cliques rápidos viram um pedido só
 * (espera de meio segundo) e o pedido anterior é cancelado. Os endereços dos PDFs
 * guardados são liberados ao sair da tela.
 *
 * Sem nenhuma seção marcada não há pedido: sem o parâmetro, a rota devolveria o
 * PDF completo.
 */
function useClientPdfPreview(projectId: string, sections: readonly ClientPdfSection[]): Preview {
  const [preview, setPreview] = useState<Preview>({ shown: null, status: "loading" });
  const seen = useRef(new Map<string, Shown>());

  useEffect(() => {
    const cache = seen.current;
    return () => cache.forEach((pdf) => URL.revokeObjectURL(pdf.url));
  }, []);

  useEffect(() => {
    if (sections.length === 0) {
      setPreview((current) => ({ ...current, status: "empty" }));
      return;
    }
    const key = sections.join(",");
    const known = seen.current.get(key);
    if (known) {
      setPreview({ shown: known, status: "ready" });
      return;
    }
    setPreview((current) => ({ ...current, status: "loading" }));
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetchClientPdfPreview(projectId, sections, controller.signal)
        .then(({ blob, pages }) => {
          const pdf = { url: URL.createObjectURL(blob), pages };
          seen.current.set(key, pdf);
          setPreview({ shown: pdf, status: "ready" });
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setPreview((current) => ({ ...current, status: "error" }));
          }
        });
    }, PREVIEW_DELAY_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [projectId, sections]);

  return preview;
}

/**
 * O menu do myGROW recolhe enquanto a tela está aberta, para a prévia ter espaço,
 * e volta como estava ao sair (pedido do Maycon, 29/10/2026). O botão do canto
 * continua abrindo o menu para quem quiser.
 *
 * O estado e a função ficam numa referência tomada ao entrar: a função do menu
 * muda a cada abrir e fechar, e como dependência faria o efeito rodar de novo
 * (e reabrir o menu) assim que ele fechasse.
 */
function useCollapsedMenu() {
  const { open, setOpen } = useSidebar();
  const before = useRef({ open, setOpen });
  before.current.setOpen = setOpen;

  useEffect(() => {
    const menu = before.current;
    const wasOpen = menu.open;
    menu.setOpen(false);
    return () => {
      if (wasOpen) menu.setOpen(true);
    };
  }, []);
}
